import React, { useState, useEffect } from 'react'
import { Button, FormControl, InputGroup, Spinner } from 'react-bootstrap'
import { ErrorMessage, Formik } from 'formik'
import * as Yup from 'yup'
import { getCurrencySymbol } from 'constants/currency'

import { updateTeamMembers, getTeamRequests } from '../../../../services/manage'
import { PLANS, getTeamMemberPriceIdBySymbol, getAdditionalTeamMemberPrice } from 'constants/plans'

import styles from '../AddTeamMembersModal.module.css'

const schema = Yup.object({
  email: Yup.string().email('Please end a valid email').required()
})

const formatRequest = (existingTeamMembers, teamMemberList, teamMemberPriceId) => {
  const add = []
  const remove = []
  teamMemberList.map((email) => {
    if (!existingTeamMembers.includes(email)) {
      add.push(email)
    }
  })
  existingTeamMembers.map((email) => {
    if (!teamMemberList.includes(email)) {
      remove.push(email)
    }
  })
  return {
    add,
    remove,
    teamMemberPriceId
  }
}

const isListSame = (array1, array2) =>
  array1.length === array2.length && array1.every((value, index) => value === array2[index])

const Add = ({
  existingTeamMembers = [],
  teamMemberLimit,
  onHide,
  onOperationComplete,
  onOperationFailed,
  userProfile
}) => {
  const [loading, setLoading] = useState(false)
  const [teamMemberList, setTeamMemberList] = useState(existingTeamMembers)
  const [pendingRequests, setPendingRequests] = useState([])
  const [requestsLoading, setRequestsLoading] = useState(false)
  const [requestError, setRequestError] = useState('')

  const teamMemberPriceId = getTeamMemberPriceIdBySymbol(userProfile?.currency)
  const teamMemberPrice = getAdditionalTeamMemberPrice(userProfile?.currency)
  const currency = getCurrencySymbol(userProfile?.currency)
  const isFreeAccount = !userProfile?.productId

  useEffect(() => {
    if ([PLANS.PRO_U.id, PLANS.PRO_MAX.id].includes(userProfile?.productId)) {
      fetchPendingRequests()
    }
  }, [userProfile])

  const fetchPendingRequests = async () => {
    setRequestsLoading(true)
    setRequestError('')
    try {
      const { data } = await getTeamRequests()
      setPendingRequests(data.requests || [])
    } catch (error) {
      console.error('Failed to load pending requests:', error)
      setRequestError('Failed to load pending requests')
    } finally {
      setRequestsLoading(false)
    }
  }

  const handleApproveRequest = (request) => {
    setPendingRequests((prev) => prev.filter((r) => r.id !== request.id))
    addMember(request.requesterEmail)
  }

  const updateMembers = () => {
    setLoading(true)
    const request = formatRequest(existingTeamMembers, teamMemberList, teamMemberPriceId)

    updateTeamMembers(request)
      .then(({ data }) => {
        setLoading(false)
        const rejected = Array.isArray(data?.rejected) ? data.rejected : []
        const rejectedEmails = rejected.map(({ email }) => email?.toLowerCase())
        const isManual = Boolean(data?.manual)
        const invitedEmails = isManual
          ? []
          : (Array.isArray(data?.added) ? data.added : request.add).filter(
              (email) => typeof email === 'string' && !rejectedEmails.includes(email.toLowerCase())
            )
        const removedCount = Array.isArray(request.remove) ? request.remove.length : 0

        const result = {
          addedCount: invitedEmails.length,
          invitedEmails,
          removedCount,
          isManual,
          rejected
        }

        onOperationComplete(result)
      })
      .catch((error) => {
        setLoading(false)
        onOperationFailed(error?.response?.data?.code)
      })
  }

  const removeMember = (email) => setTeamMemberList(teamMemberList.filter((item) => item !== email))

  const addMember = (email) => {
    if (!teamMemberList.includes(email.toLowerCase())) {
      setTeamMemberList([...teamMemberList, email.toLowerCase()])
    }
  }
  return (
    <>
      <div className="p-4">
        <h2 className="mb-2 bold color-purple-2 font-22">Manage team members</h2>
        <div className="grey">Add or remove team members who have access to this account.</div>
        <div className="mt-4">
          {teamMemberList.map((email) => (
            <div className="flex mt-1 align-items-center" key={email}>
              <div className="flex-grow-1">{email}</div>
              <Button variant="outline-primary" size="sm" onClick={() => removeMember(email)}>
                Remove
              </Button>
            </div>
          ))}
          {teamMemberList.length === 0 && (
            <span>
              <i>You have no team members</i>
            </span>
          )}
        </div>

        {/* Suggested Team Members Section - Only for PRO users */}
        {userProfile?.potentialTeamMembers &&
          userProfile.potentialTeamMembers.length > 0 &&
          [PLANS.PRO_U.id, PLANS.PRO_MAX.id].includes(userProfile?.productId) && (
            <div className="mt-4">
              <h6 className="color-purple-2 font-16 mb-3">
                <img src="/assets/icons/team.svg" width="16" height="16" className="me-2" alt="team" />
                Suggested colleagues
              </h6>
              <div className="bg-light rounded p-3">
                <div className="small text-muted mb-2">
                  These colleagues from your company domain are already using Tiiny Host:
                </div>
                {userProfile.potentialTeamMembers.slice(0, 5).map((member) => {
                  return (
                    <div className="flex mt-2 align-items-center" key={member.email}>
                      <div className="flex-grow-1">
                        <span className="font-weight-medium">{member.email}</span>
                        {member.name && <div className="small text-muted">{member.name}</div>}
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => addMember(member.email)}
                        disabled={teamMemberList.includes(member.email)}
                      >
                        {teamMemberList.includes(member.email) ? 'Added' : 'Add'}
                      </Button>
                    </div>
                  )
                })}
                {userProfile.potentialTeamMembers.length > 5 && (
                  <div className="small text-muted mt-2">
                    +{userProfile.potentialTeamMembers.length - 5} more colleagues available
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Pending Team Requests Section - Only for PRO users */}
        {pendingRequests.length > 0 && [PLANS.PRO_U.id, PLANS.PRO_MAX.id].includes(userProfile?.productId) && (
          <div className="mt-4">
            <h6 className="color-purple-2 font-16 mb-3">
              <img src="/assets/icons/team.svg" width="16" height="16" className="me-2" alt="team" />
              Pending team requests
            </h6>
            <div className="bg-light rounded p-3">
              {requestError && <div className="small text-danger mb-2">{requestError}</div>}
              <div className="small text-muted mb-2">These users have requested to join your team:</div>
              {requestsLoading ? (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                pendingRequests.map((request) => (
                  <div className="flex mt-2 align-items-center" key={request.id}>
                    <div className="flex-grow-1">
                      <span className="font-weight-medium">{request.requesterEmail}</span>
                      <div className="small text-muted">Requested to join your team</div>
                    </div>
                    <div className="d-flex gap-2">
                      <Button variant="primary" size="sm" onClick={() => handleApproveRequest(request)}>
                        Add to team
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        <hr />
        <div className="grey small mt-2 ms-1 me-1">
          <img src="/assets/icons/info-grey.svg" height={12} className="me-2" alt="info" />
          {!isFreeAccount && (
            <>
              You can add up to{' '}
              <b>
                {teamMemberLimit - existingTeamMembers.length} additional team member
                {teamMemberLimit - existingTeamMembers.length !== 1 ? 's' : ''}
              </b>{' '}
              in your current plan or purchase additional seats if you need more.
            </>
          )}
        </div>
        <Formik
          validationSchema={schema}
          onSubmit={({ email }, { setFieldValue, setFieldTouched }) => {
            addMember(email)
            setFieldTouched('email', false)
            setFieldValue('email', '')
          }}
          initialValues={{}}
          enableReinitialize
        >
          {({ handleSubmit, handleChange, values }) => (
            <form onSubmit={handleSubmit}>
              <InputGroup>
                <FormControl
                  name="email"
                  type="email"
                  placeholder="Email"
                  value={values.email}
                  onChange={handleChange}
                />
                <Button type="submit" variant="outline-primary">
                  Add
                </Button>
              </InputGroup>
              <ErrorMessage name="email" component="div" className="validation-error" />
            </form>
          )}
        </Formik>
        <hr />
        {!isFreeAccount && teamMemberList.length > teamMemberLimit && (
          <div className={styles.chargeText}>
            <div>
              You will be billed an{' '}
              <b>
                additional {currency}
                {teamMemberPrice} per month
              </b>{' '}
              <b>pro-rata basis starting today</b>.
            </div>
            <span className="op-6 font-small">We will automatically charge your current payment method.</span>
          </div>
        )}
        <div className="mt-3">
          <Button
            className="float-right"
            type="submit"
            disabled={loading || isListSame(existingTeamMembers, teamMemberList)}
            variant="primary"
            onClick={updateMembers}
          >
            {loading && <Spinner size="sm" animation="border" />}
            {!loading && 'Update'}
          </Button>
          <Button className="float-right me-2" disabled={loading} variant="outline-primary" onClick={onHide}>
            Cancel
          </Button>
        </div>
      </div>
    </>
  )
}

export default Add
